
"use client"

import React, { useState, useRef, useEffect } from "react"
import { X, ArrowLeft, ArrowRight, Info } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface TipStep {
  target: string
  title: string
  content: string
  position?: "top" | "right" | "bottom" | "left"
}

interface InfoTipProps {
  steps: TipStep[]
  onComplete?: () => void
  autoStart?: boolean
  className?: string
}

export function InfoTip({
  steps,
  onComplete,
  autoStart = false,
  className,
}: InfoTipProps) {
  const [isOpen, setIsOpen] = useState(autoStart)
  const [currentStep, setCurrentStep] = useState(0)
  const [tipPosition, setTipPosition] = useState({ top: 0, left: 0 })
  const tipRef = useRef<HTMLDivElement>(null)

  const calculatePosition = () => {
    if (!steps.length) return

    const targetId = steps[currentStep].target
    const targetElement = document.getElementById(targetId) || document.querySelector(targetId)
    const position = steps[currentStep].position || "bottom"

    if (!targetElement || !tipRef.current) return

    const targetRect = targetElement.getBoundingClientRect()
    const tipRect = tipRef.current.getBoundingClientRect()
    
    let top = 0
    let left = 0

    switch (position) {
      case "top":
        top = targetRect.top - tipRect.height - 10
        left = targetRect.left + (targetRect.width / 2) - (tipRect.width / 2)
        break
      case "right":
        top = targetRect.top + (targetRect.height / 2) - (tipRect.height / 2)
        left = targetRect.right + 10
        break
      case "bottom":
        top = targetRect.bottom + 10
        left = targetRect.left + (targetRect.width / 2) - (tipRect.width / 2)
        break
      case "left":
        top = targetRect.top + (targetRect.height / 2) - (tipRect.height / 2)
        left = targetRect.left - tipRect.width - 10
        break
    }

    // Adjust if tip would be off-screen
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    if (left < 10) left = 10
    if (left + tipRect.width > viewportWidth - 10) left = viewportWidth - tipRect.width - 10
    if (top < 10) top = 10
    if (top + tipRect.height > viewportHeight - 10) top = viewportHeight - tipRect.height - 10

    setTipPosition({ top, left })
  }

  useEffect(() => {
    if (isOpen) {
      calculatePosition()
      // Recalculate position on window resize
      window.addEventListener("resize", calculatePosition)
      // Recalculate position on scroll
      window.addEventListener("scroll", calculatePosition)
      
      return () => {
        window.removeEventListener("resize", calculatePosition)
        window.removeEventListener("scroll", calculatePosition)
      }
    }
  }, [isOpen, currentStep])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      closeTip()
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const closeTip = () => {
    setIsOpen(false)
    setCurrentStep(0)
    if (onComplete) onComplete()
  }

  const openTip = () => {
    setIsOpen(true)
    setCurrentStep(0)
  }

  const getPositionArrowStyles = () => {
    const position = steps[currentStep]?.position || "bottom"
    
    switch (position) {
      case "top":
        return "after:top-full after:left-1/2 after:-translate-x-1/2 after:border-t-background after:border-t-8 after:border-x-transparent after:border-x-8 after:border-b-0"
      case "right":
        return "after:right-full after:top-1/2 after:-translate-y-1/2 after:border-r-background after:border-r-8 after:border-y-transparent after:border-y-8 after:border-l-0"
      case "bottom":
        return "after:bottom-full after:left-1/2 after:-translate-x-1/2 after:border-b-background after:border-b-8 after:border-x-transparent after:border-x-8 after:border-t-0"
      case "left":
        return "after:left-full after:top-1/2 after:-translate-y-1/2 after:border-l-background after:border-l-8 after:border-y-transparent after:border-y-8 after:border-r-0"
    }
  }

  if (!steps.length) return null

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={cn("rounded-full", className)}
        onClick={openTip}
        aria-label="Show information"
      >
        <Info className="h-5 w-5" />
      </Button>

      {isOpen && (
        <div
          className="fixed z-50 animate-fade-in"
          style={{
            top: `${tipPosition.top}px`,
            left: `${tipPosition.left}px`,
          }}
          ref={tipRef}
        >
          <div 
            className={cn(
              "relative w-[280px] rounded-lg border bg-background p-4 shadow-lg after:absolute after:h-0 after:w-0",
              getPositionArrowStyles()
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-5 w-5 rounded-full p-0"
              onClick={closeTip}
            >
              <X className="h-3 w-3" />
            </Button>
            
            <div className="mb-4 mt-1">
              <h4 className="mb-1 text-sm font-semibold">{steps[currentStep].title}</h4>
              <p className="text-xs text-muted-foreground">{steps[currentStep].content}</p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>{currentStep + 1}</span>
                <span>/</span>
                <span>{steps.length}</span>
              </div>
              
              <div className="flex items-center gap-1">
                {currentStep > 0 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handlePrevious}>
                    <ArrowLeft className="mr-1 h-3 w-3" />
                    Previous
                  </Button>
                )}
                
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleNext}>
                  {currentStep < steps.length - 1 ? (
                    <>
                      Next
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </>
                  ) : (
                    "Finish"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
